import {
  MediaQuery,
  Burger,
  Text,
  Grid,
  Anchor,
  Button,
  Kbd,
  ActionIcon,
  Indicator,
  Group,
  Menu,
  Divider,
  useMantineColorScheme,
} from '@mantine/core';
import { openSpotlight } from '@mantine/spotlight';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Bell,
  Box,
  ChevronDown,
  FileText,
  Language,
  MoonStars,
  Plus,
  Search,
  Sun,
  Upload,
} from 'tabler-icons-react';

import { useGetNotifications } from 'api/notifications/getNotifications';
import NotificationCenter from 'components/NotificationCenter/NotificationCenter';
import QuickCreateIssue from 'components/QuickCreateIssue';
import { SUPPORTED_UI_LANGUAGES } from 'i18n/languages';
import { User } from 'types';

import styles from './Header.module.css';

type HeaderProps = {
  opened: boolean;
  onToggleNavbar: () => void;
  onClose: () => void;
  user: User;
};

function Header({ opened, onToggleNavbar, onClose, user }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const colorSchemeState = useMantineColorScheme();
  const handleToggleColorScheme = () => { colorSchemeState.toggleColorScheme(); };
  const { data } = useGetNotifications(false, undefined, 10);
  const [notificationCenterOpened, setNotificationCenterOpened] = useState(false);
  const unreadCount = data?.unreadCount || 0;

  return (
    <Grid align="center" h="100%" m={0} className={styles.header}>
      <Grid.Col span={8} sm="content" p={0} order={2} orderSm={1}>
        <Anchor
          component={Link}
          to="/dashboard"
          underline={false}
          onClick={onClose}
          className={styles.link}
        >
          <div className={styles['logo-container']}>
            <MediaQuery largerThan={768} styles={{ display: 'none' }}>
              <Box size={34} color="#845EF7" />
            </MediaQuery>
            <MediaQuery smallerThan={768} styles={{ display: 'none' }}>
              <Box size={48} color="#845EF7" />
            </MediaQuery>
            <Text className={styles.logo}>FlowBoard</Text>
          </div>
        </Anchor>
      </Grid.Col>
      <Grid.Col span={2} sm="auto" p={0} order={1} orderSm={2}>
        <MediaQuery largerThan={768} styles={{ display: 'none' }}>
          <Burger
            opened={opened}
            onClick={onToggleNavbar}
            size="sm"
            color="#2C2E33"
            aria-label={t('header.openNav')}
          />
        </MediaQuery>
      </Grid.Col>
      <Grid.Col span={2} sm="content" p={0} pr={8} order={3}>
        <MediaQuery smallerThan={768} styles={{ display: 'none' }}>
          <Group spacing={10}>
            <Menu position="bottom-end" width={260} shadow="md">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  radius={6}
                  size={36}
                  aria-label={t('language.menu')}
                  type="button"
                >
                  <Language size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown style={{ maxHeight: 380, overflowY: 'auto' }}>
                <Menu.Label>{t('language.menu')}</Menu.Label>
                {SUPPORTED_UI_LANGUAGES.map((code) => (
                  <Menu.Item
                    key={code}
                    onClick={() => {
                      i18n.changeLanguage(code).catch(() => {});
                    }}
                  >
                    {t(`language.${code}`)}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
            <ActionIcon
              variant="subtle"
              radius={6}
              size={36}
              onClick={handleToggleColorScheme}
              aria-label={
                colorSchemeState.colorScheme === 'dark'
                  ? t('header.switchToLight')
                  : t('header.switchToDark')
              }
            >
              {colorSchemeState.colorScheme === 'dark' ? <Sun size={18} /> : <MoonStars size={18} />}
            </ActionIcon>
            <QuickCreateIssue user={user}>
              {({ open, openBulkCreate }) => (
                <Menu position="bottom-end" width={220} shadow="md">
                  <Menu.Target>
                    <Button
                      variant="filled"
                      color="violet"
                      radius={6}
                      leftIcon={<Plus size={18} />}
                      rightIcon={<ChevronDown size={16} />}
                      type="button"
                      aria-label={t('header.createMenuAria')}
                      styles={{ root: { fontWeight: 500 } }}
                    >
                      {t('header.create')}
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      icon={<FileText size={16} />}
                      onClick={open}
                    >
                      {t('header.createIssue')}
                    </Menu.Item>
                    <Menu.Item
                      icon={<Upload size={16} />}
                      onClick={openBulkCreate}
                    >
                      {t('header.bulkCreateCsv')}
                    </Menu.Item>
                    <Divider my="xs" />
                    <Menu.Label>
                      <Group spacing={4}>
                        <Kbd>Ctrl</Kbd>
                        <span>+</span>
                        <Kbd>I</Kbd>
                        <Text size="xs" color="dimmed">
                          {t('header.createIssueShortcutHint')}
                        </Text>
                      </Group>
                    </Menu.Label>
                  </Menu.Dropdown>
                </Menu>
              )}
            </QuickCreateIssue>
            <Indicator label={unreadCount} size={16} disabled={!unreadCount}>
              <ActionIcon
                variant="outline"
                radius={6}
                aria-label={t('header.notifications')}
                onClick={() => setNotificationCenterOpened(true)}
              >
                <Bell size={18} />
              </ActionIcon>
            </Indicator>
            <Button
              variant="outline"
              radius={6}
              fw={400}
              aria-label={t('header.searchIssuesProjectsAria')}
              leftIcon={<Search size={16} color="#373A40" />}
              rightIcon={<Kbd>Ctrl + K</Kbd>}
              styles={(theme) => ({
                root: {
                  borderColor: theme.colors.gray[4],
                  color: '#767676',
                  '&:active': {
                    transform: 'none',
                  },
                },
                label: {
                  marginRight: 50,
                },
              })}
              onClick={() => openSpotlight()}
            >
              {t('header.searchIssues')}
            </Button>
          </Group>
        </MediaQuery>
        <MediaQuery largerThan={768} styles={{ display: 'none' }}>
          <Group spacing={8}>
            <Menu position="bottom-end" width={260} shadow="md">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  radius={6}
                  size={36}
                  aria-label={t('language.menu')}
                  type="button"
                >
                  <Language size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown style={{ maxHeight: 380, overflowY: 'auto' }}>
                <Menu.Label>{t('language.menu')}</Menu.Label>
                {SUPPORTED_UI_LANGUAGES.map((code) => (
                  <Menu.Item
                    key={code}
                    onClick={() => {
                      i18n.changeLanguage(code).catch(() => {});
                    }}
                  >
                    {t(`language.${code}`)}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
            <ActionIcon
              variant="subtle"
              radius={6}
              size={36}
              onClick={handleToggleColorScheme}
              aria-label={
                colorSchemeState.colorScheme === 'dark'
                  ? t('header.switchToLight')
                  : t('header.switchToDark')
              }
            >
              {colorSchemeState.colorScheme === 'dark' ? <Sun size={18} /> : <MoonStars size={18} />}
            </ActionIcon>
            <QuickCreateIssue user={user}>
              {({ open, openBulkCreate }) => (
                <Menu position="bottom-end" width={220} shadow="md">
                  <Menu.Target>
                    <ActionIcon
                      variant="filled"
                      color="violet"
                      radius={6}
                      aria-label={t('header.createMobileAria')}
                      type="button"
                    >
                      <Plus size={18} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item icon={<FileText size={16} />} onClick={open}>
                      {t('header.createIssue')}
                    </Menu.Item>
                    <Menu.Item
                      icon={<Upload size={16} />}
                      onClick={openBulkCreate}
                    >
                      {t('header.bulkCreateCsv')}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </QuickCreateIssue>
            <Indicator label={unreadCount} size={16} disabled={!unreadCount}>
              <ActionIcon
                variant="outline"
                radius={6}
                aria-label={t('header.notifications')}
                onClick={() => setNotificationCenterOpened(true)}
              >
                <Bell size={18} />
              </ActionIcon>
            </Indicator>
          </Group>
        </MediaQuery>
      </Grid.Col>
      <NotificationCenter
        opened={notificationCenterOpened}
        onClose={() => setNotificationCenterOpened(false)}
      />
    </Grid>
  );
}

export default Header;
